import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { getRequestIdentityHeader } from '@meitu/xdesign-identity';
import message from '@/ComUI/message';
import { getConfig } from '@/configProvider';
import { ENV_ENUM, getApiHost } from '@/utils/env';
import { getClientLanguage, getCountryCode } from '.';
import { getLocaleStore } from '../i18n';
import { getWPSAccessToken } from '../wps/utils';

declare module 'axios' {
    interface AxiosRequestConfig {
        hiddenMessage?: boolean;
        directReturn?: boolean;
    }
}

export const getCommonParams = () => {
    return {
        gid: getConfig().gid,
        education_role: getConfig().userInfo?.education_role ?? '',
        client_id: getConfig().client_id,
        preview_mode: getConfig().env === ENV_ENUM.release ? 0 : 1,
        client_language: getClientLanguage(getLocaleStore().locale),
        country_code: getCountryCode(),
        ts_random_id: uuid(),
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
};

const request = axios.create({
    withCredentials: true,
});

const getDefaultError = () => {
    const { $t } = getLocaleStore();
    return {
        code: 1024,
        message: $t((v) => v.utils.requestErrorText),
    };
};

export function getAccessToken() {
    const { isWpsUser } = getConfig();

    if (isWpsUser) {
        const wpsAccessToken = getWPSAccessToken();

        return wpsAccessToken || '';
    }

    if (typeof window !== 'undefined') {
        return window.localStorage.getItem('access_token') || '';
    }
    return '';
}

/** 用户被团队移除的状态码 */
export const RESIGNED_CODE = 402;

request.interceptors.request.use(
    (config) => {
        config.baseURL = config.baseURL ?? getApiHost();
        const customHeader =
            typeof window === 'undefined'
                ? {}
                : {
                      access_token: getAccessToken(),
                      ...getRequestIdentityHeader(),
                  };

        config.headers = {
            ...customHeader,
            ...config.headers.common,
            ...config.headers,
        };
        config.params = {
            ...getCommonParams(),
            ...config.params,
        };
        return config;
    },
    () => Promise.reject(getDefaultError()),
);

request.interceptors.response.use(
    async (response) => {
        // 直接返回数据
        if (response.config?.directReturn) {
            return response.data;
        }
        const code = response.data.code;
        const messageText = response.data.message || getDefaultError().message;
        if (code) {
            !response?.config?.hiddenMessage && message.error(messageText);
            throw { code, message: messageText };
        }
        return response.data?.data;
    },
    (error) => {
        const data = error?.response?.data || getDefaultError();
        const code = data.code;
        const messageText = data.message;
        if (!error?.config?.hiddenMessage) {
            message.error(messageText);
        }
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ code, message: messageText });
    },
);

export default request;
